/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useMemo, useRef } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import Select from "react-select";
import ImageInput from "../../../../common/ImageInput";
import PlaceSearchInput from "../../../../common/PlaceSearchInput";
import ToggleButton from "../../../../common/ToggleButton";
import FilePreview from "../../../../FilePreview/FilePreview";
import TinyEditor from "../../../../Editor/TinyEditor";
import {
    getActivityCategories,
    createActivityCategory,
    getActivityById,
    deleteActivityGalleryImage
} from "../activityService";
import InlineCategoryCreateDialog, {
    slugifyCategoryName,
} from "../../../../common/InlineCategoryCreateDialog";
import { abEditorWrap } from "../activityBuilderUi";
import { useFormHydrationGuard } from "../../../../../utils/useFormHydrationGuard";

// Age limit options (dropdown)
const AGE_LIMIT_OPTIONS = [
    { value: "", label: "No age restriction" },
    { value: "0",  label: "All ages" },
    { value: "3",  label: "3+ years" },
    { value: "5",  label: "5+ years" },
    { value: "7",  label: "7+ years" },
    { value: "10", label: "10+ years" },
    { value: "12", label: "12+ years" },
    { value: "14", label: "14+ years" },
    { value: "16", label: "16+ years" },
    { value: "18", label: "18+ years" },
    { value: "21", label: "21+ years" },
];

const DURATION_UNITS = {
  Minutes: "Minutes",
  Hours: "Hours",
  Days: "Days",
};

const durationDisplayToMinutes = (value, unit) => {
  if (value === "" || value == null) return "";
  const num = parseFloat(value);
  if (Number.isNaN(num) || num < 0) return null;
  if (unit === DURATION_UNITS.Hours) return Math.round(num * 60);
  if (unit === DURATION_UNITS.Days) return Math.round(num * 60 * 24);
  return Math.round(num);
};

const minutesToDurationDisplay = (minutes) => {
  const dur = Number(minutes);
  if (!Number.isFinite(dur) || dur <= 0) {
    return { unit: DURATION_UNITS.Minutes, input: "" };
  }
  if (dur >= 60 * 24 && dur % (60 * 24) === 0) {
    return { unit: DURATION_UNITS.Days, input: String(dur / (60 * 24)) };
  }
  if (dur >= 60 && dur % 60 === 0) {
    return { unit: DURATION_UNITS.Hours, input: String(dur / 60) };
  }
  return { unit: DURATION_UNITS.Minutes, input: String(dur) };
};

const GeneralInfo = ({
    setData,
    activityId,
    onLoadingChange,
    validationErrors,
    submitAttempted = false,
    onValidityChange,
    variant = "all",
    sharedFormData,
    setSharedFormData,
    formDataRef,
    onFormChange,
    skipServerHydration = false,
    onUserEdit,
}) => {
    const showGeneral = variant === "all" || variant === "general";
    const showMedia = variant === "all" || variant === "media";
    const usesLocalFormState = Boolean(formDataRef) || !setSharedFormData;
    const parentOwnsFormState = Boolean(setSharedFormData) && !formDataRef;
    const { markEdited, shouldBlockHydration, markHydrated } = useFormHydrationGuard(activityId);

    const createEmptyFormState = () => ({
        name: "",
        activity_category_id: "",
        description: "",
        thumb_image: null,
        cover_image: null,
        gallery_images: [],
        gallery: [],
        duration: "",
        location: "",
        latitude: "",
        longitude: "",
        map_link: "",
        age_limit: "",
        layout: "indoor",
        free_booking: 0,
        refundable: 0,
        time_slot_based: 0,
        status: 1,
        is_active: 1,
        current_stage: 1,
    });

    const [localFormData, setLocalFormData] = useState(() => {
        if (formDataRef?.current) {
            return { ...createEmptyFormState(), ...formDataRef.current };
        }
        return createEmptyFormState();
    });

    const formData = usesLocalFormState ? localFormData : (sharedFormData ?? localFormData);
    const setFormData = usesLocalFormState ? setLocalFormData : (setSharedFormData ?? setLocalFormData);

    const [durationUnit, setDurationUnit] = useState(DURATION_UNITS.Minutes);
    const [durationInput, setDurationInput] = useState("");
    const durationSyncForActivityRef = useRef(null);
    const [touched, setTouched] = useState({});
    const [clientErrors, setClientErrors] = useState({});
    const [categoryDialogOpen, setCategoryDialogOpen] = useState(false);
    const queryClient = useQueryClient();

    const createActivityCategoryInline = async ({ name, image }) => {
        const formDataPayload = new FormData();
        formDataPayload.append("name", name);
        formDataPayload.append("slug", slugifyCategoryName(name));
        formDataPayload.append("status", 1);
        const imageFile =
            image instanceof File ? image : image?.file instanceof File ? image.file : null;
        if (imageFile) {
            formDataPayload.append("image", imageFile);
        }
        return createActivityCategory(formDataPayload);
    };

    const markTouched = (field) => {
        setTouched((prev) => ({ ...prev, [field]: true }));
    };

    const validate = (data) => {
        const errors = {};
        const req = (field, message) => {
            if (
                data[field] === null ||
                data[field] === undefined ||
                (typeof data[field] === "string" && data[field].trim() === "")
            ) {
                errors[field] = message;
            }
        };

        req("name", "Activity name is required.");
        req("activity_category_id", "Category is required.");
        req("location", "Location is required.");
        req("latitude", "Location latitude is required.");
        req("longitude", "Location longitude is required.");
        req("map_link", "Map link is required.");

        const duration = Number(data.duration);
        if (!Number.isFinite(duration) || duration < 1) {
            errors.duration = "Duration must be at least 1 minute.";
        }

        return errors;
    };

    const notifyUserEdit = () => {
        markEdited();
        onUserEdit?.();
    };

    useEffect(() => {
        if (!formDataRef) return;
        formDataRef.current = formData;
        onFormChange?.(formData);
    }, [formData, formDataRef, onFormChange]);

    // Sync to parent (standalone tabs only — builder uses formDataRef as single source of truth)
    useEffect(() => {
        if (parentOwnsFormState || formDataRef || !setData) return;
        setData(formData);
    }, [formData, setData, parentOwnsFormState, formDataRef]);

    useEffect(() => {
        const errs = validate(formData);
        setClientErrors(errs);
        const fieldsValid = Object.keys(errs).length === 0;
        const hasMedia =
            Boolean(formData.thumb_image) && Boolean(formData.cover_image);

        if (variant === "media") {
            onValidityChange?.(hasMedia);
        } else if (variant === "all") {
            onValidityChange?.(fieldsValid && hasMedia);
        } else {
            onValidityChange?.(fieldsValid);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formData, variant]);

    // Fetch Categories
    const { data: categoriesData } = useQuery({
        queryKey: ["activityCategories"],
        queryFn: getActivityCategories,
    });

    const categories = useMemo(() => {
        if (!categoriesData) return [];
        if (
            categoriesData.data &&
            categoriesData.data.data &&
            Array.isArray(categoriesData.data.data)
        ) {
            return categoriesData.data.data;
        }
        if (Array.isArray(categoriesData)) return categoriesData;
        if (categoriesData.data && Array.isArray(categoriesData.data))
            return categoriesData.data;
        return [];
    }, [categoriesData]);

    const categoryOptions = categories.map((cat) => ({
        value: cat.id,
        label: cat.name,
    }));

    // Fetch Activity Data if ID exists
    const { data: activityData, isLoading: activityLoading } = useQuery({
        queryKey: ["activity", activityId],
        queryFn: () => getActivityById(activityId),
        enabled: !!activityId,
    });

    // Notify parent about loading
    useEffect(() => {
        if (onLoadingChange) {
            onLoadingChange(activityLoading);
        }
    }, [activityLoading, onLoadingChange]);

    /** Apply GET /activities/:id once per edit session — refetches were resetting gallery + staged uploads. */
    const hydratedServerSnapshotForIdRef = useRef(null);

    useEffect(() => {
        hydratedServerSnapshotForIdRef.current = null;
        durationSyncForActivityRef.current = null;
    }, [activityId]);

    // Populate form data from GET /activities/:id (standalone + builder with formDataRef).
    useEffect(() => {
        if (parentOwnsFormState) return;
        if (!activityId || !activityData?.data) return;
        if (shouldBlockHydration(activityId)) return;

        const data = activityData.data;
        const serverGallery = Array.isArray(data.gallery_images)
            ? data.gallery_images
            : Array.isArray(data.galleryImages)
            ? data.galleryImages
            : [];

        if (hydratedServerSnapshotForIdRef.current === activityId) {
            setFormData((prev) => {
                if (
                    serverGallery.length > 0 &&
                    (!Array.isArray(prev.gallery) || prev.gallery.length === 0)
                ) {
                    return { ...prev, gallery: serverGallery };
                }
                return prev;
            });
            return;
        }

        hydratedServerSnapshotForIdRef.current = activityId;
        markHydrated(activityId);

        setFormData((prev) => ({
            ...prev,
            name: data.name || "",
            activity_category_id: data.activity_category_id || "",
            description: data.description || "",
            thumb_image: data.thumb_image || null,
            cover_image: data.cover_image || null,
            location: data.location || "",
            latitude: data.latitude || "",
            longitude: data.longitude || "",
            map_link: data.map_link || "",
            duration: data.duration ?? 0,
            layout: data.layout || "indoor",
            free_booking: Number(data.free_booking) === 1 || data.free_booking === true ? 1 : 0,
            refundable: Number(data.refundable) === 1 || data.refundable === true ? 1 : 0,
            time_slot_based: Number(data.time_slot_based) === 1 || data.time_slot_based === true ? 1 : 0,
            gallery: serverGallery,
            gallery_images: [],
            age_limit: data.age_limit != null ? String(data.age_limit) : "",
        }));
        const { unit, input } = minutesToDurationDisplay(data.duration);
        setDurationUnit(unit);
        setDurationInput(input);
        durationSyncForActivityRef.current = activityId;
    }, [activityId, activityData, parentOwnsFormState, shouldBlockHydration, markHydrated, formDataRef]);

    useEffect(() => {
        if (durationSyncForActivityRef.current === activityId) return;

        if (formData.duration === "" || formData.duration == null) {
            if (!activityId) {
                setDurationInput("");
                setDurationUnit(DURATION_UNITS.Minutes);
                durationSyncForActivityRef.current = activityId;
            }
            return;
        }

        const dur = Number(formData.duration);
        if (!Number.isFinite(dur) || dur <= 0) return;

        const { unit, input } = minutesToDurationDisplay(dur);
        setDurationUnit(unit);
        setDurationInput(input);
        durationSyncForActivityRef.current = activityId;
    }, [activityId, formData.duration]);

    const handleChange = (field, value) => {
        notifyUserEdit();
        setFormData((prev) => ({ ...prev, [field]: value }));
    };

    const handleLocationChange = (place) => {
        notifyUserEdit();
        if (place) {
            const mapLink = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(place.name)}&query_place_id=${place.place_id}`;
            setFormData(prev => ({
                ...prev,
                location: place.name,
                latitude: place.lat,
                longitude: place.lng,
                map_link: mapLink
            }));
        } else {
            setFormData(prev => ({
                ...prev,
                location: "",
                latitude: "",
                longitude: "",
                map_link: ""
            }));
        }
    };

    const handleDurationValueChange = (val) => {
        notifyUserEdit();
        setDurationInput(val);
        const minutes = durationDisplayToMinutes(val, durationUnit);
        if (minutes === null) return;
        setFormData((prev) => ({ ...prev, duration: minutes }));
    };

    const handleDurationUnitChange = (unit) => {
        notifyUserEdit();
        setDurationUnit(unit);
        const minutes = durationDisplayToMinutes(durationInput, unit);
        if (minutes === null) return;
        setFormData((prev) => ({ ...prev, duration: minutes }));
    };

    // Handle adding gallery images (append using functional update so refetches can't strand stale batches)
    const handleAddGalleryImages = (newImages) => {
        if (newImages == null) return;
        const batch = Array.isArray(newImages) ? newImages : [newImages];
        const files = batch.filter((f) => f instanceof File);
        if (files.length === 0) return;

        notifyUserEdit();
        setFormData((prev) => ({
            ...prev,
            gallery_images: [...(prev.gallery_images || []), ...files],
        }));
    };

    // Handle removing existing gallery image
    const handleRemoveExistingGalleryImage = async (index, imageId) => {
        const previousGallery = formData.gallery || [];

        setFormData((prev) => ({
            ...prev,
            gallery: (prev.gallery || []).filter((_, i) => i !== index),
        }));

        if (!activityId) return;

        if (imageId) {
            try {
                await deleteActivityGalleryImage(activityId, imageId);
                await queryClient.invalidateQueries({ queryKey: ["activity", activityId] });
            } catch (error) {
                console.error("Error removing gallery image:", error);
                setFormData((prev) => ({ ...prev, gallery: previousGallery }));
            }
            return;
        }

        await queryClient.invalidateQueries({ queryKey: ["activity", activityId] });
    };

    // Handle removing new gallery image
    const handleRemoveNewGalleryImage = (index) => {
        const updatedImages = formData.gallery_images.filter((_, i) => i !== index);
        setFormData((prev) => ({
            ...prev,
            gallery_images: updatedImages,
        }));
    };

    const getImagePreviewProps = (image) => {
        if (!image) return { file: null, fileUrl: null };
        if (image instanceof File) return { file: image, fileUrl: null };
        if (typeof image === "string") return { file: null, fileUrl: image };
        const url = image?.image_url || image?.url || image?.image || null;
        return { file: null, fileUrl: typeof url === "string" ? url : null };
    };

    const thumbPreviewUrl =
        formData.thumb_image instanceof File
            ? null
            : typeof formData.thumb_image === "string"
            ? formData.thumb_image
            : null;
    const coverPreviewUrl =
        formData.cover_image instanceof File
            ? null
            : typeof formData.cover_image === "string"
            ? formData.cover_image
            : null;

    const getSelectErrorStyles = (fieldName) => ({
        control: (base, state) => ({
            ...base,
            minHeight: "32px",
            height: "32px",
            backgroundColor: "#ffffff !important",
            borderColor: validationErrors?.[fieldName] ? "#ef4444" : "#d1d5db",
            borderRadius: "0.5rem",
            boxShadow: state.isFocused ? "none" : "none",
            "&:hover": {
                borderColor: validationErrors?.[fieldName] ? "#ef4444" : "#d1d5db",
                backgroundColor: "#ffffff !important",
            },
            "&:focus": {
                borderColor: "primary",
                boxShadow: "none",
                backgroundColor: "#ffffff !important",
            },
        }),
        valueContainer: (base) => ({
            ...base,
            padding: "0 8px",
            fontSize: "13px",
            height: "30px",
        }),
        input: (base) => ({
            ...base,
            margin: 0,
            padding: "0",
            fontSize: "13px",
        }),
        placeholder: (base) => ({
            ...base,
            fontSize: "14px",
        }),
        singleValue: (base) => ({
            ...base,
            fontSize: "14px",
            fontWeight: "500",
        }),
        indicatorsContainer: (base) => ({
            ...base,
            height: "30px",
        }),
        indicatorSeparator: () => ({
            display: "none",
        }),
        dropdownIndicator: (base) => ({
            ...base,
            padding: "0 8px",
        }),
        clearIndicator: (base) => ({
            ...base,
            padding: "0 8px",
        }),
        menu: (base) => ({
            ...base,
            fontSize: "13px",
        }),
    });

    const inputClasses = (error) => `placeholder:text-sm text-sm font-medium text-gray-800 placeholder:font-normal w-full px-3 py-1 border ${error ? "border-red-500" : "border-gray-300"
        } rounded-lg focus:outline-none focus:border-primary focus:ring-none`;

    return (
        <div className="space-y-5">
            {showGeneral && (
            <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Name */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Activity Name <span className="text-red-500">*</span>
                        </label>
                        <input
                            type="text"
                            value={formData.name || ""}
                            onChange={(e) => handleChange("name", e.target.value)}
                            onBlur={() => markTouched("name")}
                            className={inputClasses(validationErrors?.name)}
                            placeholder="Enter activity name"
                        />
                        {(validationErrors?.name || (clientErrors.name && (touched.name || submitAttempted))) && (
                            <p className="text-red-500 text-xs mt-1">
                                {validationErrors?.name?.[0] || clientErrors.name}
                            </p>
                        )}
                    </div>

                    {/* Category */}
                    <div>
                        <div className="mb-1 flex items-center justify-between">
                            <label className="block text-sm font-medium text-gray-700">
                                Category <span className="text-red-500">*</span>
                            </label>
                            <button
                                type="button"
                                onClick={() => setCategoryDialogOpen(true)}
                                className="text-xs font-semibold text-primary hover:underline"
                            >
                                + Add Category
                            </button>
                        </div>
                        <Select
                            options={categoryOptions}
                            value={categoryOptions.find(
                                (op) => String(op.value) === String(formData.activity_category_id)
                            ) || null}
                            onChange={(op) => handleChange("activity_category_id", op?.value)}
                            styles={getSelectErrorStyles("activity_category_id")}
                            placeholder="Select Category"
                            onBlur={() => markTouched("activity_category_id")}
                        />
                        {(validationErrors?.activity_category_id ||
                            (clientErrors.activity_category_id &&
                                (touched.activity_category_id || submitAttempted))) && (
                            <p className="text-red-500 text-xs mt-1">
                                {validationErrors?.activity_category_id?.[0] ||
                                    clientErrors.activity_category_id}
                            </p>
                        )}
                    </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    {/* Duration */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Duration
                        </label>
                        <div className="flex gap-2">
                            <div className="flex-1">
                                <input
                                    type="number"
                                    min="0"
                                    value={durationInput}
                                    onChange={(e) => handleDurationValueChange(e.target.value)}
                                    onBlur={() => markTouched("duration")}
                                    className={inputClasses(validationErrors?.duration)}
                                    placeholder="Value"
                                />
                                {(validationErrors?.duration ||
                                    (clientErrors.duration &&
                                        (touched.duration || submitAttempted))) && (
                                    <p className="text-red-500 text-xs mt-1">
                                        {validationErrors?.duration?.[0] || clientErrors.duration}
                                    </p>
                                )}
                            </div>
                            <div className="w-1/3">
                                <select
                                    value={durationUnit}
                                    onChange={(e) => handleDurationUnitChange(e.target.value)}
                                    className="w-full h-[34px] px-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:border-primary bg-white"
                                >
                                    <option value="Minutes">Mins</option>
                                    <option value="Hours">Hrs</option>
                                    <option value="Days">Days</option>
                                </select>
                            </div>
                        </div>
                    </div>

                    {/* Layout */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Layout
                        </label>
                        <select
                            value={formData.layout || "indoor"}
                            onChange={(e) => handleChange("layout", e.target.value)}
                            className="w-full h-[34px] px-3 text-sm border border-gray-300 rounded-lg focus:outline-none focus:border-primary bg-white"
                        >
                            <option value="indoor">Indoor</option>
                            <option value="outdoor">Outdoor</option>
                        </select>
                    </div>

                    {/* Age Limit — dropdown */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Age Limit
                        </label>
                        <select
                            value={formData.age_limit ?? ""}
                            onChange={(e) => handleChange("age_limit", e.target.value)}
                            onBlur={() => markTouched("age_limit")}
                            className="w-full h-[34px] px-3 text-sm border border-gray-300 rounded-lg focus:outline-none focus:border-primary bg-white"
                        >
                            {AGE_LIMIT_OPTIONS.map((opt) => (
                                <option key={opt.value} value={opt.value}>
                                    {opt.label}
                                </option>
                            ))}
                        </select>
                    </div>
                </div>

                {/* Description */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        Description
                    </label>
                    <div
                        className={`${abEditorWrap} ${
                            validationErrors?.description ? "ring-1 ring-red-500" : ""
                        }`}
                    >
                        <TinyEditor
                            className="package-field-text"
                            value={formData.description || ""}
                            onChange={(content) => handleChange("description", content)}
                            height={320}
                        />
                    </div>
                    {validationErrors?.description && (
                        <p className="text-red-500 text-xs mt-1">
                            {validationErrors.description[0]}
                        </p>
                    )}
                </div>
            </div>
            )}

            {showMedia && (
            <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Thumbnail */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Thumbnail Image <span className="text-red-500">*</span>
                        </label>
                        <ImageInput
                            onChange={(image) => {
                                notifyUserEdit();
                                setFormData((prev) => ({
                                    ...prev,
                                    thumb_image: image,
                                }));
                            }}
                            existingImage={thumbPreviewUrl}
                            error={validationErrors?.thumb_image}
                            label=""
                            required={false}
                            className="w-full"
                            multiple={false}
                            showPreview={false}
                        />
                         {validationErrors?.thumb_image && (
                            <p className="text-red-500 text-xs mt-1">
                                {validationErrors.thumb_image[0]}
                            </p>
                        )}
                        <p className="text-xs text-gray-500 mt-1">
                            Recommended size: 300x300px
                        </p>
                    </div>

                    {/* Cover */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Cover Image <span className="text-red-500">*</span>
                        </label>
                        <ImageInput
                            onChange={(image) => {
                                notifyUserEdit();
                                setFormData((prev) => ({
                                    ...prev,
                                    cover_image: image,
                                }));
                            }}
                            existingImage={coverPreviewUrl}
                            error={validationErrors?.cover_image}
                            label=""
                            required={false}
                            className="w-full"
                            multiple={false}
                            showPreview={false}
                        />
                        {validationErrors?.cover_image && (
                            <p className="text-red-500 text-xs mt-1">
                                {validationErrors.cover_image[0]}
                            </p>
                        )}
                        <p className="text-xs text-gray-500 mt-1">
                            Recommended size: 1200x600px
                        </p>
                    </div>
                </div>

                {(formData.thumb_image || formData.cover_image) && (
                    <div className="mt-4">
                        <p className="mb-2 text-sm font-bold text-gray-900">
                            Thumbnail & Cover Preview
                        </p>
                        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
                            {formData.thumb_image && (
                                <FilePreview
                                    {...getImagePreviewProps(formData.thumb_image)}
                                    label="Thumbnail"
                                    onDelete={() =>
                                        setFormData((prev) => ({
                                            ...prev,
                                            thumb_image: null,
                                        }))
                                    }
                                    isImage={true}
                                />
                            )}
                            {formData.cover_image && (
                                <FilePreview
                                    {...getImagePreviewProps(formData.cover_image)}
                                    label="Cover"
                                    onDelete={() =>
                                        setFormData((prev) => ({
                                            ...prev,
                                            cover_image: null,
                                        }))
                                    }
                                    isImage={true}
                                />
                            )}
                        </div>
                    </div>
                )}

                {/* Gallery */}
                <div className="mt-6">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        Gallery Images
                    </label>
                    <ImageInput
                        onChange={handleAddGalleryImages}
                        error={validationErrors?.gallery_images}
                        label=""
                        required={false}
                        className="w-full"
                        multiple={true}
                        showPreview={false}
                    />
                    {validationErrors?.gallery_images && (
                        <p className="text-red-500 text-xs mt-1">
                            {validationErrors.gallery_images[0]}
                        </p>
                    )}
                    <p className="text-xs text-gray-500 mt-1">
                        You can select multiple images for the activity gallery
                    </p>

                    {(formData.gallery.length > 0 || formData.gallery_images.length > 0) && (
                        <div className="mt-4">
                            <p className="mb-2 text-sm font-bold text-gray-900">
                                Gallery Images Preview
                            </p>
                            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
                                {formData.gallery.map((image, index) => (
                                    <FilePreview
                                        key={image?.id ? `existing-${image.id}` : `existing-${index}`}
                                        {...getImagePreviewProps(image)}
                                        label={`Gallery ${index + 1}`}
                                        onDelete={() =>
                                            handleRemoveExistingGalleryImage(
                                                index,
                                                image?.id || null
                                            )
                                        }
                                        isImage={true}
                                    />
                                ))}
                                {formData.gallery_images.map((image, index) => (
                                    <FilePreview
                                        key={`new-${index}`}
                                        {...getImagePreviewProps(image)}
                                        label={`New Gallery ${index + 1}`}
                                        onDelete={() => handleRemoveNewGalleryImage(index)}
                                        isImage={true}
                                    />
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            </div>
            )}

            {showGeneral && (
            <div className="space-y-4">
                {/* Location */}
                <div className="mb-6">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        Location <span className="text-red-500">*</span>
                    </label>
                    <PlaceSearchInput
                        value={formData.location || ""}
                        onChange={handleLocationChange}
                        placeholder="Search Location"
                        hasError={
                            !!validationErrors?.location ||
                            !!(clientErrors.location && (touched.location || submitAttempted))
                        }
                    />
                    {(validationErrors?.location ||
                        (clientErrors.location &&
                            (touched.location || submitAttempted))) && (
                        <p className="text-red-500 text-xs mt-1">
                            {validationErrors?.location?.[0] || clientErrors.location}
                        </p>
                    )}
                </div>

                {/* Features */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-4 border-t border-gray-100">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Free Booking
                        </label>
                        <ToggleButton
                            enabled={Number(formData.free_booking) === 1}
                            onChange={(val) => handleChange("free_booking", val ? 1 : 0)}
                        />
                        {Number(formData.free_booking) === 1 && (
                            <p className="text-xs text-blue-600 mt-1">Pricing step will be skipped for free activities.</p>
                        )}
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Time-slot-based activity
                        </label>
                        <ToggleButton
                            enabled={Number(formData.time_slot_based) === 1}
                            onChange={(val) => handleChange("time_slot_based", val ? 1 : 0)}
                        />
                    </div>
                </div>
            </div>
            )}
            <InlineCategoryCreateDialog
                open={categoryDialogOpen}
                onClose={() => setCategoryDialogOpen(false)}
                onCreated={(newId) => handleChange("activity_category_id", newId)}
                createFn={createActivityCategoryInline}
                queryKey={["activityCategories"]}
                requiresImage
                title="Add Activity Category"
            />
        </div>
    );
};

export default GeneralInfo;
